import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { goToTopOfPage, setCurrentStoryStep } from '../../../helper/helper';
import { saveCharacterForImagePrompt } from '../services/builderHelpers';
import { CONFIG } from '../../../config/mainConfig';
import { Toast } from '../../../components/Common/Toast';
import Heading from '../../../components/Common/Heading';
import Guide from '../../../components/Common/Guide';
import Loader from '../../../components/Common/Loader';
import Dialogs from '../../../components/Common/Dialog';
import { CharacterModel } from '../../../interfaces/character.interface';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  SelectedMainCharacterPopupEnum,
  setCurrentStep,
  setEnsureToUseSelectedMainCharacterPopup,
} from '../../../store/appSlice';
import { setStoryData } from '../../../store/storySlice';
import { CharacterLibraryProps } from '../models/CharacterLibraryProps.interface';
import { deleteCharacter } from '../services/character.service';
import { useStorageObjectUrl } from '../../../services/file.service';

type CharacterCardProps = {
  character: CharacterModel;
  isSelected: boolean;
  className: string;
  setSelectedPortrait: (c: CharacterModel) => void;
  setOpenDialog: (c: boolean) => void;
  selectedPortrait: CharacterModel | null;
};

export const CharacterCard: React.FC<CharacterCardProps> = ({
  character,
  isSelected,
  className,
  setSelectedPortrait,
  setOpenDialog,
  selectedPortrait,
}: CharacterCardProps) => {
  const { url, loading, error } = useStorageObjectUrl(character.characterUrl);

  const imageClass = isSelected ? 'border-4 border-storyblue' : '';
  const imageURL = url || character.characterUrl || '/loading.gif';

  return (
    <div
      className={`${className} ${imageClass}`}
      style={{
        backgroundImage: `url('${imageURL}')`,
        backgroundSize: 'contain',
      }}
      onClick={() => {
        setSelectedPortrait(character);
      }}
    >
      <FontAwesomeIcon
        icon={faTrashCan}
        className={`text-[#EF4444] text-lg cursor-pointer ${
          selectedPortrait ? 'ml-64 md:ml-40' : 'ml-60 md:ml-40'
        } mt-4`}
        onClick={() => {
          setSelectedPortrait(character);
          setOpenDialog(true);
        }}
      />
    </div>
  );
};

const CharacterLibrary = ({
  getCharacterLibary,
  loaders,
  library,
  setLibrary,
  setUploadedImageUrl,
  setShowCharacterLibrary,
  currentPortrait,
  setCurrentPortrait,
  className = `w-72 h-72 md:w-48 md:h-48 rounded-lg hover:border-4 hover:border-storygreen bg-gray-200 cursor-pointer`,
  currentStep,
  setLoaders,
  setStateData,
  stateData,
  artStyle,
  selectedArtStyle,
  startStepFour,
  resetCharacter,
}: CharacterLibraryProps) => {
  const user = useSelector((state: RootState) => state.user.user);
  const [showBackButton, setShowBackButton] = useState<boolean>(false);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [selectedPortrait, setSelectedPortrait] = useState<CharacterModel | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<string>('');
  const storyData = useSelector((state: RootState) => state.story.storyData);
  const artStyleById = useSelector((state: RootState) => state.app.artStyleById);
  const ensureToUseSelectedMainCharacterPopup = useSelector(
    (state: RootState) => state.app.ensureToUseSelectedMainCharacterPopup,
  );
  const dispatch: AppDispatch = useDispatch();

  const handleDeleteConfirmed = async () => {
    await deleteCharacter(
      user?.email!,
      selectedPortrait?.characterUrl,
      getCharacterLibary,
      loaders,
      setLoaders,
      setLibrary,
      setSelectedPortrait,
      setOpenDialog,
    );
  };

  useEffect(() => {
    goToTopOfPage();
    getCharacterLibary(user?.email!, loaders, setLoaders, setLibrary, artStyle);
  }, []);

  useEffect(() => {
    if (sortOption) {
      if (sortOption === 'recent') {
        setLibrary([...library]?.sort((a, b) => a.timestamp - b.timestamp));
      } else if (sortOption === 'name') {
        setLibrary([...library]?.sort((a, b) => a.characterName.localeCompare(b.characterName)));
      }
    }
  }, [sortOption, library]);

  const selectExistCharacterAndStartStepFour = async () => {
    if (!storyData?.illustrationCharacter?.pageIndex) {
      setUploadedImageUrl(selectedPortrait?.characterUrl!); //this must be set to view in Builder
      setCurrentPortrait(selectedPortrait?.characterUrl!);
      setStateData({ ...stateData, characterId: selectedPortrait?.characterId });
      setShowCharacterLibrary(false);
      setTimeout(() => startStepFour());
    } else {
      /**
       * Navigate to Generate New Illustration Component
       */
      await saveCharacterForImagePrompt(
        storyData?.illustrationCharacter?.bookId,
        selectedPortrait?.characterUrl!,
        storyData?.illustrationCharacter?.pageIndex,
        selectedPortrait!,
        { age: '', gender: '', mics: '' },
      );

      setUploadedImageUrl('');
      setShowCharacterLibrary(false);
      dispatch(setCurrentStep(3));
      // Ping storyData so the StoryPages useEffect re-evaluates illustrationCharacter
      // and re-opens the edit illustration modal for this page.
      dispatch(setStoryData({ lastHandledErrorAt: Date.now() }));
    }
    setTimeout(() => setIsSelected(true));
    setCurrentStoryStep(CONFIG.STORY_STEPS.ILLUSTRATION_CREATE);
  };

  useEffect(() => {
    if (
      ensureToUseSelectedMainCharacterPopup?.current === SelectedMainCharacterPopupEnum.SUBMIT &&
      ensureToUseSelectedMainCharacterPopup?.previous === SelectedMainCharacterPopupEnum.EXIST
    ) {
      selectExistCharacterAndStartStepFour().then().catch();
    }
  }, [ensureToUseSelectedMainCharacterPopup, selectExistCharacterAndStartStepFour]);

  const confirmStartStepFour = () => {
    dispatch(setEnsureToUseSelectedMainCharacterPopup(SelectedMainCharacterPopupEnum.EXIST));
  };

  return (
    <div className="text-left m-2 mb-8 text-sm text-storyblue animate__animated animate__slideInRight">
      <div className="flex justify-between">
        <div className=" flex items-center align-end mb-4">
          <Heading title="Character Library" />
          <div>
            <Guide />
          </div>
        </div>
        <div className="gap-2">
          <button
            className="bg-storyblue text-white font-bold rounded-2xl px-4 py-2 text-sm"
            onClick={() => {
              setShowCharacterLibrary(false);
            }}
          >
            Close
          </button>
        </div>
      </div>

      {loaders.CharacterLibrary ? (
        <div className="flex justify-center">
          <Loader loader={loaders.CharacterLibrary} />
        </div>
      ) : (
        <>
          <div className="w-full flex md:flex-nowrap flex-wrap justify-between items-center gap-4 my-4">
            <p className="text-left text-sm text-storyblue">
              Select an existing Character or create a new One. To create a new character, click on Generate New
              Character button at bottom! Happy story building
            </p>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="rounded-md text-base h-10"
            >
              <option value="" disabled>
                Sort By
              </option>
              <option value="recent">Sort by Recent</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
          <div className="w-full grid grid-cols-1s md:grid-cols-3 lg:grid-cols-6 gap-2 overflow-y-auto p-0 md:pr-2 h-[20rem]">
            {library &&
              library?.length > 0 &&
              library
                ?.filter((character: CharacterModel) => character.artStyle === selectedArtStyle?.id)
                ?.map((character: CharacterModel, index: number) => {
                  const isSelected = selectedPortrait === (character || currentPortrait);
                  const imageClass = isSelected ? 'border-4 border-storyblue' : selectedPortrait ? 'opacity-50' : '';
                  return (
                    <div className="flex flex-col gap-2 mx-auto">
                      <CharacterCard
                        character={character}
                        className={`${className} ${imageClass}`}
                        selectedPortrait={selectedPortrait}
                        isSelected={isSelected}
                        setSelectedPortrait={setSelectedPortrait}
                        setOpenDialog={setOpenDialog}
                      ></CharacterCard>
                      <p className="capitalize">
                        <strong>Character Name:</strong> {character?.characterName}
                      </p>
                      <p className="capitalize">
                        <strong className="mr-1">Art style:</strong>
                        {(character?.artStyle && artStyleById[character?.artStyle]) ||
                          character?.artStyle?.split(/[-_]/)?.join(' ') ||
                          ''}
                      </p>
                    </div>
                  );
                })}
          </div>
          <div className="flex w-full justify-center gap-4 flex-wrap md:flex-nowrap">
            <button
              disabled={!selectedPortrait}
              onClick={async () => {
                if (selectedPortrait?.artStyle === selectedArtStyle?.id) {
                  if (!storyData?.illustrationCharacter?.pageIndex) {
                    confirmStartStepFour();
                  } else {
                    await selectExistCharacterAndStartStepFour();
                  }
                } else {
                  setShowBackButton(true);
                  Toast('Selected Character Artstyle is Different Than Chosen Artstyle', true);
                }
              }}
              className={`mt-4 submit-button bg-storyblue text-white font-bold rounded-2xl px-4 py-2 text-base ${
                selectedPortrait ? '' : 'opacity-50'
              }`}
            >
              Select Existing Character
            </button>
            <button
              onClick={() => {
                resetCharacter();
              }}
              className="md:mt-4 submit-button bg-storyblueSecondLightText text-white font-bold rounded-2xl px-4 py-2 text-base"
            >
              Generate New Character
            </button>
          </div>
        </>
      )}
      <Dialogs
        openDialog={openDialog}
        handelClose={() => setOpenDialog(false)}
        handelProcess={handleDeleteConfirmed}
        confirmMessage="Are you sure you want to delete this character?"
      />
    </div>
  );
};
export default CharacterLibrary;
